import { Typography, Box, Tab, Tabs } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import { EditButton } from "common/components/EditButton";
import { Comment } from "./Comment";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Backdrop } from "common/components/Backdrop";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { createSelector } from "reselect";
import { getDeviceAlertDescription } from "safetySensors/deviceAlerts";
import {
  formatUnixToDateString,
  getSensorTypeNameOnDeviceType,
} from "../functions/sensorFunctions";
import { IApplicationState } from "../../redux/root-reducer";
import {
  getAllSensorDataForSingleNode,
  getComments,
  getHistoryForSensor,
} from "../Sensors.actions";
import styles from "./DetailedSafetySensor.module.scss";
import { EventHistory } from "./EventHistory";
import { StatusIndicator } from "./statusIndicator";
import { t } from "i18next";
import { NewComment } from "comments/components/newComment";
import { isLandlordTypeGuard } from "common/models/userInfo";
import { IMeterEventsObject } from "meter/types";
import moment, { Moment } from "moment";

const getDomainSelector = createSelector(
  (state: IApplicationState) => state.common,
  (commonState) => ({
    domainCode: commonState.selectedDomain?.Code ?? "",
    isLandLord: isLandlordTypeGuard(commonState.currentUserInfo),
    userIsReady: commonState.userIsReady,
  })
);
const sensorsForNodeSelector = (currentMeasuringPointId: string) =>
  createSelector(
    (state: IApplicationState) => state.sensors,
    (allSensorDataForSingleNode) => ({
      sensor: allSensorDataForSingleNode.allSensorDataForSingleNode.find(
        (sensor) =>
          sensor.MeasuringPointID.toString() === currentMeasuringPointId
      ),
      sensorComments: allSensorDataForSingleNode.sensorComments,
      isFetching: allSensorDataForSingleNode.isFetching,
    })
  );

export const DetailedSafetySensor = () => {
  const [openNewComment, setOpenNewComment] = React.useState<boolean>(false);
  const closeDialog = () => {
    setOpenNewComment(false);
  };
  const openDialog = () => {
    setOpenNewComment(true);
  };
  const [alarm, setAlarm] = useState(false);
  const [datafetched, setDataFetched] = useState(false);
  const [todaysDate, setTodaysDate] = useState<Moment>(moment());
  const [activeTab, setActiveTab] = useState<string>(t("History"));
  const dispatch = useDispatch();
  const { domainCode, isLandLord, userIsReady } =
    useSelector(getDomainSelector);

  const { id, currentSensorDetail }: any = useParams();
  const { sensor, isFetching, sensorComments } = useSelector(
    sensorsForNodeSelector(currentSensorDetail)
  );
  useEffect(() => {
    if (domainCode) {
      if (userIsReady && domainCode && id && !sensor) {
        dispatch(getAllSensorDataForSingleNode(domainCode, id ?? ""));
      }
      if (sensor?.Alert) {
        setAlarm(true);
      }
    }
  }, [userIsReady, domainCode, id, sensor, dispatch]);

  useEffect(() => {
    if (activeTab === t("History")) {
      if (sensor && !datafetched) {
        dispatch(
          getHistoryForSensor(domainCode, id, sensor?.MeasuringPointID!)
        );
        setDataFetched(true);
      }
    } else {
      dispatch(getComments(domainCode, sensor?.MeasuringPointID!));
    }
  }, [activeTab, sensor]);

  const handleUpdate = () => {
    if (isLandLord) {
      dispatch(getComments(domainCode, sensor?.MeasuringPointID!));
    }
    dispatch(getHistoryForSensor(domainCode, id, sensor?.MeasuringPointID!));
    setTodaysDate(moment());
  };

  if (isFetching || !sensor) return <Backdrop open={isFetching} />;
  let previousEvents: IMeterEventsObject[] = [];
  if (sensor.PreviousEvents) {
    previousEvents = [...sensor.PreviousEvents].sort((a, b) =>
      a.ChangeTime > b.ChangeTime ? -1 : b.ChangeTime > a.ChangeTime ? 1 : 0
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.titles}>
        <Typography variant="h1">{sensor.MeasuringPointName}</Typography>
        <Typography variant="h3">
          {getSensorTypeNameOnDeviceType(sensor.DeviceType)}
        </Typography>
      </div>
      <div className={styles.informationContainer}>
        <div className={styles.row}>
          <span className={styles.label}>{t("Status")}</span>
          {alarm ? (
            <StatusIndicator
              status="warning"
              text={sensor?.Alert?.Description}
            />
          ) : (
            <StatusIndicator status="ok" text={t("Everything OK")} />
          )}
        </div>
        {isLandLord && (
          <div className={styles.row}>
            <span className={styles.label}>{t("Address")}</span>
            <span>{sensor.Address ? sensor.Address : t("Missing")}</span>
          </div>
        )}
        <div className={styles.row}>
          <span className={styles.label}>{t("Serial number")}</span>
          <span>{sensor.ExternalKey}</span>
        </div>
      </div>

      <div className={styles.history}>
        <div className={styles.updated}>
          {isLandLord && (
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(e, value) => setActiveTab(value)}
              aria-label="disabled tabs example"
            >
              <Tab label={t("History")} value={t("History")} />
              <Tab label={t("Comments")} value={t("Comments")} />
            </Tabs>
          )}

          <div className={styles.historyTitle}>
            {activeTab === t("History") ? (
              <Typography variant="h2">{t("History")}</Typography>
            ) : (
              <Typography variant="h2">{t("Comments")}</Typography>
            )}
            <Box
              display="flex"
              flex-direction="column"
              alignItems="center"
              justifyContent="center"
              padding="15px"
              boxShadow={"0px 1px 3px rgba(0, 0, 0, 0.12)"}
              borderRadius="15px"
              onClick={handleUpdate}
              bgcolor="var(--color-white)"
            >
              <Typography variant="h4">{t("Update")}</Typography>
              <RefreshIcon fontSize="small" />
            </Box>
          </div>
          <div className={styles.row}>
            <span className={styles.label} style={{ marginRight: 5 }}>
              {t("Last updated:")}
            </span>
            {formatUnixToDateString(todaysDate.unix())}
          </div>
        </div>
        <div className={styles.itemContainer}>
          {activeTab === t("History") &&
            previousEvents &&
            previousEvents.map((alert, i) => (
              <EventHistory
                key={i}
                description={sensor?.Alert?.Description || ""}
                value={alert.Value}
                date={alert.ChangeTime}
                event={
                  getDeviceAlertDescription(alert.EventTypeID) ||
                  alert.EventType
                }
              />
            ))}
          {activeTab === t("Comments") && (
            <>
              <Box onClick={openDialog}>
                <EditButton
                  title={t("Add comment")}
                  icon={<CreateIcon height={20} />}
                  iconColor="var(--color-green)"
                  onClick={openDialog}
                />
              </Box>
              {sensorComments.map((c) => {
                return <Comment key={c.ID} commentID={c.ID} />;
              })}
            </>
          )}
        </div>
      </div>
      <NewComment
        open={openNewComment}
        onClose={closeDialog}
        measuringPointId={sensor.MeasuringPointID}
      />
    </div>
  );
};
